import styled from '@emotion/styled';
import { RootStore } from '@vromo/dashboard/src/stores/RootStore';
import { useStore } from '@vromo/mobx-react/mobx-react';
import { Coords } from '@vromo/models';
import { Site } from '@vromo/models';
import FontAwesome from '@vromo/ui/components/FontAwesome';
import Geocoder, { GeocoderValue } from '@vromo/ui/components/Geocoder2';
import Input, { InputGroup, InputGroupAddon, InputLabel } from '@vromo/ui/components/Input';
import theme from '@vromo/ui/components/theme';
import { css } from 'emotion';
import { findLastIndex } from 'lodash';
import { action, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState } from 'react';
import Map from './Map';
import { qaLabelTestSites } from './../../../../../../common-qa-labels/sites';

const FilterRow = styled.div`
    display: flex;
    flex-direction: column;
    padding: 5px 0px;

    label {
        flex: 1;
    }

    input,
    select {
        margin-top: 5px;
    }
`;

type Props = {
    editedSite: Site;
};
export default observer(function EditLocation({ editedSite }: Props) {
    const { viewStore } = useStore<RootStore>('rootStore');

    const mapRef = useRef();

    const [siteAddress, setSiteAddress] = useState<GeocoderValue | null>(null);

    const [siteLat, setSiteLat] = useState<number>(editedSite.coords?.lat ?? 0);
    const [siteLon, setSiteLon] = useState<number>(editedSite.coords?.lon ?? 0);
    const [siteRadius, setSiteRadius] = useState<number>(editedSite.radius ?? 50);

    useEffect(() => {
        if (editedSite.contact?.address && editedSite.coords) {
            setSiteAddress({ label: editedSite.contact?.address, value: editedSite.coords });
        } else {
            setSiteAddress(null);
        }
    }, []);

    function updateLocation(selection: GeocoderValue | null): void {
        setSiteAddress(selection);

        if (selection) {
            const address = selection.label;
            const { lat, lon } = selection.value;

            if (lat && lon && mapRef.current && editedSite.contact?.address !== undefined) {
                runInAction(() => {
                    editedSite.contact.address = address;
                    editedSite.coords = new Coords(lat, lon);
                });

                mapRef.current.setCenter([lon, lat]);
                mapRef.current.setZoom(15);
            }
        } else {
            runInAction(() => {
                editedSite.contact.address = null;
                editedSite.coords = null;
            });

            mapRef.current.setCenter([0, 0]);
            mapRef.current.setZoom(0);
        }
    }

    return (
        <>
            <FilterRow data-testid={qaLabelTestSites.addSiteModal.addressRow}
                className={css`
                    input {
                        margin-top: 0px !important;
                    }
                `}>
                <Geocoder
                    label="Address"
                    value={siteAddress}
                    placeholder="Search for an address or place..."
                    onChange={updateLocation}
                    proximity={viewStore.location}
                />
            </FilterRow>
            <FilterRow>
                <label>Location Adjust</label>
                <div
                    style={{
                        marginBottom: 10,
                        borderRadius: 5,
                        overflow: 'auto'
                    }}>
                    <Map
                        ref={mapRef}
                        site={editedSite}
                        height={250}
                        onCenterChanged={action(([lon, lat]) => {
                            editedSite.coords = new Coords(lat, lon);
                        })}
                    />
                </div>
            </FilterRow>
            <FilterRow
                className={css`
                    flex-direction: row !important;
                    align-items: center;

                    > * {
                        margin-left: 5px;
                        flex: 1;
                    }
                `}>
                <Input
                    label="Latitude"
                    value={editedSite.coords?.lat?.toFixed(5) || 0}
                    type="number"
                    step="0.0001"
                    disabled
                    onChange={(e) => {
                        const lat = Number(e.target.value);
                        setSiteLat(e.target.value);
                        if (lat != NaN) {
                            editedSite.coords = new Coords(
                                Number(lat),
                                editedSite.coords?.lon || 0
                            );
                        }
                    }}
                />
                <Input
                    label="Longitude"
                    value={editedSite.coords?.lon?.toFixed(5) || 0}
                    type="number"
                    step="0.0001"
                    disabled
                    onChange={(e) => {
                        const lon = Number(e.target.value);
                        setSiteLon(e.target.value);
                        if (lon != NaN) {
                            editedSite.coords = new Coords(
                                editedSite.coords?.lat || 0,
                                Number(lon)
                            );
                        }
                    }}
                />
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Input
                        data-testid={qaLabelTestSites.addSiteModal.radiusInput}
                        label="Radius"
                        value={siteRadius}
                        type="number"
                        step="1"
                        onChange={(e) => {
                            const rad = Number(e.target.value);
                            setSiteRadius(e.target.value);
                            if (rad != NaN) {
                                editedSite.radius = rad;
                            }
                        }}
                    />
                    <div
                        className={css`
                            margin-top: 16px;
                            margin-left: 3px;
                            padding: 5px;
                            border-radius: 3px;
                            background-color: ${theme.lightGrey};
                            border: 1px solid ${theme.lightGrey};
                            display: flex;
                            align-items: center;
                        `}>
                        {viewStore.imperialUnits ? 'ft.' : 'm.'}
                    </div>
                </div>
            </FilterRow>
        </>
    );
});
